'use client';

import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import {
  ManageUserAttributesContextProps,
  ManageUserAttributesProps,
  userAttributesFromEndpointProps
} from './types';
import { getAttributesToManage, structureUserAttributesData } from './utils';
import { getModifiedUserAttributes, setUserAttributes } from './actions';

const ManageUserAttributesContext =
  createContext<ManageUserAttributesContextProps>(null);

export const useManageUserAttributes = () =>
  useContext(ManageUserAttributesContext);

export const ManageUserAttributesProvider: FC<
  PropsWithChildren<ManageUserAttributesProps>
> = ({ children, attributes, managedAttributes }) => {
  const modifiedUserAttributes = structureUserAttributesData(attributes);
  const attributesToManage = getAttributesToManage({
    modifiedUserAttributes,
    managedAttributes
  });

  const [managedUserAttributes, setManagedUserAttributes] =
    useState(attributesToManage);

  const updateManagedUserAttributes = useCallback(
    async (attributesToUpdate: userAttributesFromEndpointProps) => {
      const modifiedUserAttributes =
        structureUserAttributesData(attributesToUpdate);
      setManagedUserAttributes((previousState) => {
        const newState = { ...previousState, ...modifiedUserAttributes };
        return newState;
      });
      await setUserAttributes(attributesToUpdate);
    },
    []
  );

  const getManagedUserAttributes = useCallback(async () => {
    const modifiedUserAttributes = await getModifiedUserAttributes();
    const attributesToManage = getAttributesToManage({
      modifiedUserAttributes,
      managedAttributes
    });
    return attributesToManage;
  }, [managedAttributes]);

  const propsValues = useMemo(
    () => ({
      managedUserAttributes,
      updateManagedUserAttributes,
      getManagedUserAttributes
    }),
    [
      managedUserAttributes,
      updateManagedUserAttributes,
      getManagedUserAttributes
    ]
  );

  return (
    <ManageUserAttributesContext.Provider value={propsValues}>
      {children}
    </ManageUserAttributesContext.Provider>
  );
};
